import { useState, useRef, useEffect } from "react";
import "./AppEmployeeRecordProfile.css";
import { EmployeeRecord, EmployeeType, CustomProperty, CustomPropertyType, FileToCloudURL, CreateEmployeeRecordPayload, CustomPropertyValue } from "winslow-core";
import { GenericSelect, GenericSelectOption } from "../../shared/GenericSelect";
import { Button } from "../../../ds2/components/Button";
import { AppCustomProperty } from "../app-customize/AppCustomProperty";
import { AppCustomPropertyError } from "../app-customize/AppCustomPropertyError";
import { validateEmail } from "../../uiHelpers";
import { EmployeeTypeDisplayNames, ESSENTIAL_DESCRIPTIONS } from "./employeeRecordConfig";
import { postImage, postMultipleEmployeeFileUpload } from "../../../services/client";
import toast from "react-hot-toast";
import { toastFail, toastSuccess } from "../../../config";
import { useSelector } from "react-redux";
import { selectUser } from "../../../redux/features/appData";
import { useNavigate } from "react-router-dom";
import { apiCreateEmployeeRecord, apiUpdateEmployeeRecord } from "../../../services/EmployeeRecordService";
import { erIsValid } from "./employeeRecordUtils";
import { AppChanges } from "../app-changes/AppChanges";

type AppEmployeeRecordProfileProps = {
    id: string | undefined;
    employeeType: EmployeeType | undefined;
    newEmployeeRecord: Partial<EmployeeRecord>;
    originalEmployeeRecord: Partial<EmployeeRecord> | undefined;
    customProperties: CustomProperty[] | undefined;
    resetKey: number;
    onEmployeeTypeChange: (employeeType: EmployeeType) => void;
    onResetKeyChange: () => void;
    onChange: (employeeRecord: Partial<EmployeeRecord>) => void;
    onSave: (employeeRecord: Partial<EmployeeRecord>) => void;
    onUpdate: (employeeRecord: Partial<EmployeeRecord>) => void;
};

export const AppEmployeeRecordProfile = ({
    id,
    employeeType,
    newEmployeeRecord,
    originalEmployeeRecord,
    customProperties,
    resetKey,
    onEmployeeTypeChange,
    onResetKeyChange,
    onChange,
    onSave,
    onUpdate,
}: AppEmployeeRecordProfileProps) => {
    const storeUser = useSelector(selectUser);
    const navigate = useNavigate();
    const avatarFileRef = useRef<HTMLInputElement>(null);
    const [avatarFile, setAvatarFile] = useState<string>("");
    const [hasChanges, setHasChanges] = useState<boolean>(false);
    const [validate, setValidate] = useState<boolean>(false);
    
    // Local state for custom properties
    const [newCustomProperties, setNewCustomProperties] = useState<Map<string, any>>(new Map());
    const newCustomPropertiesRef = useRef<Map<string, any>>(new Map());
    
    // Track if component has been initialized to prevent onChange during initial load
    const isInitializedRef = useRef<boolean>(false);

    // Initialize custom properties from newEmployeeRecord when it changes
    useEffect(() => {
        const existingCustomProperties = new Map<string, any>();
        
        if (newEmployeeRecord.customProperties) {
            try {
                const theCustomProperties = JSON.parse(newEmployeeRecord.customProperties || "[]") as CustomPropertyValue[];
                if (Array.isArray(theCustomProperties)) {
                    theCustomProperties.forEach((cp) => {
                        existingCustomProperties.set(cp.label, cp.data);
                    });
                }
            } catch (error) {
                console.error("[AppEmployeeRecordProfile] Error parsing custom properties:", error);
            }
        }
        
        setNewCustomProperties(existingCustomProperties);
        newCustomPropertiesRef.current = existingCustomProperties;
        
        // Mark as initialized after first load
        // For existing records, wait until originalEmployeeRecord is set (data loaded)
        // For new records, mark as initialized immediately
        if (id && originalEmployeeRecord) {
            isInitializedRef.current = true;
        } else if (!id) {
            isInitializedRef.current = true;
        }
    }, [newEmployeeRecord.customProperties, id, originalEmployeeRecord]);

    // Reset hasChanges when newEmployeeRecord changes (data loaded/updated from props, not user action)
    useEffect(() => {
        setHasChanges(false);
    }, [newEmployeeRecord.workEmail, newEmployeeRecord.avatarURL, id]);

    const etOptions = Object.keys(EmployeeType).map((key) => {
        const index = key as keyof typeof EmployeeType;
        return {
            displayName: EmployeeTypeDisplayNames[index],
            value: EmployeeType[index],
            image: undefined,
            component: undefined,
        } as GenericSelectOption;
    });

    const changeAvatar = () => {
        if (avatarFileRef?.current) {
            const inputButton = avatarFileRef.current as any;
            setAvatarFile("");
            inputButton.click();
        }
    };

    const handleFileChange = async (event: any) => {
        onResetKeyChange();

        // console.log("[AppEmployeeRecordProfile/handleFileChange] here!!!");

        if (!storeUser) {
            return;
        }
        
        if (event.target.files && event.target.files?.length < 1) {
          return;
        }
    
        if (event.target.files && event.target.files[0].size > 10485760) {
          toast("Avatar is too big! Please keep it under 10MB", toastFail);
          return;
        }
    
        const theFile = event.target.files ? event.target.files[0] : null;
    
        if (!theFile) {
          toast("Did not get a valid file", toastFail);
          return;
        }

        event.target.value = null;
    
        (async () => {
          toast("Uploading...", toastSuccess);
          
          try {
            const response = await postImage(storeUser.id, theFile, ()=>{});
    
            if (!response) {
                toast("Error uploading file", toastFail);
                return;
            }
    
            // Only set hasChanges if the avatar URL actually changed
            if (response.data.data !== newEmployeeRecord.avatarURL) {
                setHasChanges(true);
                // Sync parent state immediately with new avatar URL
                syncParentState(undefined, response.data.data);
            }
            toast("Avatar uploaded successfully", toastSuccess);
    
          } catch (error) {
            console.error("[AppEmployeeRecordProfile] Error uploading avatar:", error);
            toast("Error uploading file, please try again.", toastFail);
          } finally {
            setAvatarFile("");
          }
        })();
    };

    const uploadFilesForCollection = async (uploadFiles: FileList): Promise<FileToCloudURL[] | null> => {
        if (!storeUser?.currentWorkspaceId) {
            toast("Error uploading files: No workspace ID", toastFail);
            return null;
        }
        
        if (!uploadFiles || uploadFiles.length === 0) {
            console.warn("[AppEmployeeRecordProfile] No files to upload");
            return null;
        }

        // console.log("[AppEmployeeRecordProfile/uploadFilesForCollection] uploadFiles: ", uploadFiles);

        toast(`Uploading ${uploadFiles.length} file${uploadFiles.length > 1 ? 's' : ''}...`, toastSuccess);

        try {
            const response = await postMultipleEmployeeFileUpload(
                storeUser.currentWorkspaceId,
                uploadFiles,
            );

            if (!response?.data?.data) {
                toast("Error uploading files: No response data", toastFail);
                return null;
            }

            console.log("[AppEmployeeRecordProfile] File upload successful:", response.data.data);
            return response.data.data;

        } catch (error) {
            console.error("[AppEmployeeRecordProfile] File upload error:", error);
            toast("Error uploading files. Please try again.", toastFail);
            return null;
        }
    };

    const convertPropertyMapToArray = (
        customProperties: Map<string, any>, 
        uploadedFiles: Map<string, FileToCloudURL[]>
    ): CustomPropertyValue[] => {
        const result: CustomPropertyValue[] = [];

        for (const [propertyLabel, dataValue] of customProperties) {
            if (dataValue?.newFiles || dataValue?.oldFiles) {
                const oldFiles = dataValue?.oldFiles
                const propertyValue = dataValue.newFiles
            
                // if we have new files, merge them with old
                if (propertyValue instanceof FileList) {
                    // For FILE type properties, use the uploaded cloud URLs
                    const uploadedFileURLs = uploadedFiles.get(propertyLabel);
                    if (uploadedFileURLs && uploadedFileURLs.length > 0) {
                        result.push({
                            label: propertyLabel,
                            data: [...uploadedFileURLs, ...(oldFiles || [])],
                        });
                    } else {
                        console.warn(`[AppEmployeeRecordProfile] No uploaded files found for FILE property: ${propertyLabel}`);
                    }
                } else { // we only have old files
                    result.push({
                        label: propertyLabel,
                        data: oldFiles || dataValue,
                    });
                }
            } else {
                // For non-FILE type properties, use the original value
                result.push({
                    label: propertyLabel,
                    data: dataValue,
                });
            }
        }

        return result;
    };

    const updateEmployeeRecord = async (uploadedFilesURLs: Map<string, FileToCloudURL[]>) => {
        if (!originalEmployeeRecord) {
            toast("Error updating employee, no original employee record", toastFail);
            return;
        }

        // 1. put custom props into the data object and go
        const payload : Partial<EmployeeRecord> = {
            ...newEmployeeRecord,
            workEmail: newEmployeeRecord.workEmail,
            avatarURL: newEmployeeRecord.avatarURL,
            customProperties: JSON.stringify(convertPropertyMapToArray(newCustomPropertiesRef.current, uploadedFilesURLs)),
        }

        const updatedEmployeeRecord = await apiUpdateEmployeeRecord(payload);

        if (!updatedEmployeeRecord) {
            toast("Error updating employee", toastFail);
            return;
        }

        // Update local custom properties state from the updated record
        const existingCustomProperties = new Map<string, any>();

        if (updatedEmployeeRecord.customProperties) {
            try {
                const theCustomProperties = JSON.parse(updatedEmployeeRecord.customProperties || "[]") as CustomPropertyValue[];
                if (Array.isArray(theCustomProperties)) {
                    theCustomProperties.forEach((cp) => {
                        existingCustomProperties.set(cp.label, cp.data);
                    });
                }
            } catch (error) {
                console.error("[AppEmployeeRecordProfile] Error parsing custom properties:", error);
            }
        }
        
        setNewCustomProperties(existingCustomProperties);
        newCustomPropertiesRef.current = existingCustomProperties;
        
        onUpdate(updatedEmployeeRecord);
        setHasChanges(false);
        toast("Employee updated", toastSuccess);
    };

    const handleSubmit = async () => {
        if (!employeeType) {   
            toast("Please select an employee type", toastFail);
            return;
        }

        setValidate(true);
        // console.log("[AppEmployeeRecordProfile/handleSubmit] newCustomProperties: ", newCustomPropertiesRef.current);

        const arrayOfObjects = Array.from(newCustomPropertiesRef.current, ([key, value]) => ({ label: key, data: value }));

        const cpsByType = customProperties?.filter((customProperty: CustomProperty) => {
            return customProperty.visibleEmployeeTypes.includes(employeeType)
        });

        const isValid = erIsValid({
            ...newEmployeeRecord,
            workEmail: newEmployeeRecord.workEmail,
            avatarURL: newEmployeeRecord.avatarURL,
            customProperties: JSON.stringify(arrayOfObjects)
        }, cpsByType || []);

        // console.log("[AppEmployeeRecordProfile/handleSubmit] is valid: ", isValid);

        if (!isValid) {
            toast("Please fill in all required fields and formats", toastFail);
            return;
        }

        // Upload all new FILE type custom property files and get cloud URLs
        const uploadedFilesURLs = new Map<string, FileToCloudURL[]>();
        const fileUploadPromises: Promise<void>[] = [];

        console.log("[AppEmployeeRecordProfile] newCustomProperties: ", newCustomPropertiesRef.current);
        // Process all FILE type custom properties
        for (const [propertyLabel, dataValue] of newCustomPropertiesRef.current) {
            
            const propertyValue = dataValue.newFiles;
            if (propertyValue instanceof FileList && propertyValue.length > 0) {
                console.log(`[AppEmployeeRecordProfile] Uploading ${propertyValue.length} file(s) for property: ${propertyLabel}`);
                
                const uploadPromise = uploadFilesForCollection(propertyValue)
                    .then((fileURLs: FileToCloudURL[] | null) => {
                        if (fileURLs && fileURLs.length > 0) {
                            uploadedFilesURLs.set(propertyLabel, fileURLs);
                            console.log(`[AppEmployeeRecordProfile] Successfully uploaded files for ${propertyLabel}:`, fileURLs);
                        } else {
                            console.error(`[AppEmployeeRecordProfile] Failed to upload files for ${propertyLabel}`);
                        }
                    })
                    .catch((error: any) => {
                        console.error(`[AppEmployeeRecordProfile] Error uploading files for ${propertyLabel}:`, error);
                    });
                
                fileUploadPromises.push(uploadPromise);
            }
        }

        // Wait for all file uploads to complete
        if (fileUploadPromises.length > 0) {
            try {
                await Promise.all(fileUploadPromises);
                console.log("[AppEmployeeRecordProfile] All file uploads completed:", uploadedFilesURLs);
            } catch (error) {
                console.error("[AppEmployeeRecordProfile] Error during file uploads:", error);
                toast("Error uploading some files. Please check and try again.", toastFail);
                return;
            }
        }

        // if this is an update, revector to that
        if (id) {
            updateEmployeeRecord(uploadedFilesURLs);
            return;
        }

        // 2. Create new employee record object with uploaded file URLs
        const payload: CreateEmployeeRecordPayload = {
            workspaceId: storeUser?.currentWorkspaceId || "",
            essentials: {
                ...newEmployeeRecord,
                workEmail: newEmployeeRecord.workEmail,
                avatarURL: newEmployeeRecord.avatarURL,
            },
            customProperties: JSON.stringify(convertPropertyMapToArray(newCustomPropertiesRef.current, uploadedFilesURLs)),
        }

        console.log("[AppEmployeeRecordProfile] payload: ", payload);

        const employeeRecord = await apiCreateEmployeeRecord(payload);

        if (!employeeRecord) {
            toast("Error creating employee", toastFail);
            return;
        }

        toast("Employee created", toastSuccess);
        onSave(employeeRecord);
    }

    const handleCancel = () => {
        navigate("/app/employee-records");
    }

    // Helper function to sync parent state with current local state
    const syncParentState = (updatedWorkEmail?: string, updatedAvatarURL?: string) => {
        // Don't sync during initial load to prevent resetting state before data is loaded
        if (!isInitializedRef.current) {
            console.log("[AppEmployeeRecordProfile] Skipping syncParentState during initialization");
            return;
        }
        
        const arrayOfObjects = Array.from(newCustomPropertiesRef.current, ([key, value]) => ({ label: key, data: value }));
        onChange({
            ...newEmployeeRecord,
            workEmail: updatedWorkEmail !== undefined ? updatedWorkEmail : (newEmployeeRecord.workEmail || ""),
            avatarURL: updatedAvatarURL !== undefined ? updatedAvatarURL : (newEmployeeRecord.avatarURL || ""),
            customProperties: JSON.stringify(arrayOfObjects),
        });
    };

    const handleWorkEmailChange = (email: string) => {
        // Only set hasChanges if the value actually changed
        if (email !== (newEmployeeRecord.workEmail || "")) {
            setHasChanges(true);
            // Sync parent state immediately
            syncParentState(email);
        }
    };

    const handleCustomPropertyChange = (label: string, data: any) => {
        // console.log("[AppEmployeeRecordProfile/handleCustomPropertyChange] label: ", label);
        // console.log("[AppEmployeeRecordProfile/handleCustomPropertyChange] data: ", data);
        // console.log("[AppEmployeeRecordProfile/handleCustomPropertyChange] newCustomPropertiesRef.current: ", newCustomPropertiesRef.current);
        const currentValue = newCustomPropertiesRef.current.get(label);
        
        // Only set hasChanges if the value actually changed
        // Deep comparison for objects/arrays, simple comparison for primitives
        const hasChanged = JSON.stringify(currentValue) !== JSON.stringify(data);
        
        if (hasChanged) {
            const updatedCustomProperties = new Map(newCustomPropertiesRef.current);
            updatedCustomProperties.set(label, data);
            setNewCustomProperties(updatedCustomProperties);
            newCustomPropertiesRef.current = updatedCustomProperties;
            setHasChanges(true);
            // Sync parent state immediately
            syncParentState();
        }
    };

    console.error("[AppEmployeeRecordProfile] newEmployeeRecord: ", newEmployeeRecord);
    console.error("[AppEmployeeRecordProfile] originalEmployeeRecord: ", originalEmployeeRecord);
    // console.log("[AppEmployeeRecordProfile] customProperties: ", customProperties);
    // console.log("[AppEmployeeRecordProfile] employeeType: ", employeeType);

    if (!employeeType || !newEmployeeRecord) {
        return null;
    }

    return (
        <>
            <div className="app-employee-record-essentials">
                <div className="app-employee-record-property">
                    <div className="app-employee-record-left">
                        <div className="app-employee-record-label">
                            Employee Type
                        </div>
                    </div>
                    <div className="app-employee-record-middle">
                        <div className="app-employee-record-value">
                            <GenericSelect
                                options={etOptions}
                                selected={employeeType ? etOptions.find((option) => option.value === employeeType) : undefined}
                                onChange={(value: EmployeeType) => {
                                    // Only set hasChanges if the employee type actually changed
                                    if (value !== employeeType) {
                                        setHasChanges(true);
                                    }
                                    onEmployeeTypeChange(value);
                                }}
                            />
                        </div>
                    </div>
                    <div className="app-employee-record-right">
                        {ESSENTIAL_DESCRIPTIONS.employeeType}
                    </div>
                </div>

                <div className="app-employee-record-property">
                    <div className="app-employee-record-left">
                        <div className="app-employee-record-label">
                            Profile Picture
                        </div>
                    </div>
                    <div className="app-employee-record-middle">
                        <div className="app-employee-record-value">
                            <div className="app-employee-record-avatar">
                                {newEmployeeRecord.avatarURL &&
                                    <img src={newEmployeeRecord.avatarURL} alt="Profile"/>
                                }
                                <input
                                    key={"filereset-" + resetKey}
                                    type="file"
                                    accept="image/png, image/jpeg, image/jpg, image/gif"
                                    ref={avatarFileRef}
                                    style={{ display: "none" }}
                                    value={avatarFile}
                                    onChange={handleFileChange}
                                    onError={(event) => console.log("onError", event)}
                                />
                                <Button
                                    variant="secondary"
                                    onClick={changeAvatar}
                                >
                                    Upload
                                </Button>
                            </div>
                        </div>
                    </div>
                    <div className="app-employee-record-right">
                        {ESSENTIAL_DESCRIPTIONS.avatarURL}
                    </div>
                </div>

                <div className="app-employee-record-property">
                    <div className="app-employee-record-left">
                        <div className="app-employee-record-label">
                            Work Email
                        </div>
                    </div>
                    <div className="app-employee-record-middle">
                        <div className="app-employee-record-value">
                            <input
                                type="text"
                                value={newEmployeeRecord.workEmail || ""}
                                name="workEmail"
                                onChange={(e: any) => handleWorkEmailChange(e.target.value)}
                            />
                            <AppCustomPropertyError 
                                flagRequired={validate && !newEmployeeRecord.workEmail}
                                flagError={!!(validate && newEmployeeRecord.workEmail && !validateEmail(newEmployeeRecord.workEmail))}
                                errorString="Invalid email"
                            />
                        </div>
                    </div>
                    <div className="app-employee-record-right">
                        {ESSENTIAL_DESCRIPTIONS.workEmail}
                    </div>
                </div>
            </div>

            <div className="app-employee-record-custom">
                {customProperties?.filter((customProperty: CustomProperty) => {
                        return customProperty.visibleEmployeeTypes.includes(employeeType) && !customProperty.hidden
                    })
                    .map((customProperty: CustomProperty, index: number) => {
                        return (
                            <div key={customProperty.label} className="app-employee-record-property"> 
                                <div className="app-employee-record-left">
                                    <div className="app-employee-record-label">
                                        {customProperty.display}
                                    </div>
                                    {!customProperty.required &&
                                        <div className="app-employee-record-optional">
                                            OPTIONAL
                                        </div>
                                    }
                                </div>
                                <div className="app-employee-record-middle">
                                    <AppCustomProperty
                                        key={"cp-" + customProperty.label}
                                        customProperty={customProperty}
                                        data={newCustomProperties.get(customProperty.label) || undefined}
                                        validate={validate}
                                        onChange={(label: string, data: any) => {
                                            handleCustomPropertyChange(label, data);
                                        }}
                                    />
                                </div>
                                <div className="app-employee-record-right">
                                    {customProperty.description}
                                </div>
                            </div>
                        );
                    })
                }
            </div>
            <div style={{paddingBottom: "200px"}}>{ " "}</div>
            {hasChanges &&
                <AppChanges 
                    submitText={id ? "Update" : "Add Employee"}
                    onSubmit={handleSubmit}
                    onCancel={handleCancel}
                />
            }
        </>
    );
};

